import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  saveCurrentProject,
  getCurrentProject,
  removeCurrentProject,
} from '../utils/localStorage';
import { useProject } from '../hooks/useProject';
import { useUser } from './UserContext';

interface ProjectContextType {
  projectList: any[];
  selectedProjectId: number | null;
  selectedProjectName: string | null;
  isProjectReady: boolean;
  setSelectedProject: (id: number, name: string) => void;
  clearSelectedProject: () => void;
  fetchProjects: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const tenantId = user?.tenant_id;

  const { projectList, fetchProjects, isProjectListLoaded } = useProject(tenantId);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);
  const [isProjectReady, setIsProjectReady] = useState(false);
  const [isSelectionCleared, setIsSelectionCleared] = useState(false);

  // 初回localStorageから復元
  useEffect(() => {
    if (!tenantId) {
      setIsSelectionCleared(false);
      setSelectedProjectId(null);
      setSelectedProjectName(null);
      setIsProjectReady(false);
      return;
    }
    if (!isProjectListLoaded) {
      setIsProjectReady(false);
      return;
    }
    if (isSelectionCleared) {
      setIsProjectReady(true);
      return;
    }
    const current = getCurrentProject();
    if (current) {
      setSelectedProjectId(current.id);
      setSelectedProjectName(current.name);
    } else {
      setSelectedProjectId(null);
      setSelectedProjectName(null);
    }
    setIsProjectReady(true);
  }, [tenantId, projectList, isProjectListLoaded, isSelectionCleared]);

  const setSelectedProject = (id: number, name: string) => {
    setIsSelectionCleared(false);
    saveCurrentProject(id, name);
    setSelectedProjectId(id);
    setSelectedProjectName(name);
  };

  const clearSelectedProject = () => {
    setIsSelectionCleared(true);
    removeCurrentProject();
    setSelectedProjectId(null);
    setSelectedProjectName(null);
  };

  return (
    <ProjectContext.Provider
      value={{
        projectList,
        selectedProjectId,
        selectedProjectName,
        isProjectReady,
        setSelectedProject,
        clearSelectedProject,
        fetchProjects,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjectContext must be used within an ProjectProvider');
  }
  return context;
};
